import { type ReactNode, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { Button, Drawer, Flex, Typography } from "antd";
import { ArrowRightOutlined, MenuOutlined } from "@ant-design/icons";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { GetCurrentLoggedUserDocument } from "../../gql/generated/graphqlSchema.tsx";
import { routes } from "../../routes.ts";
import { LanguageSwitcher } from "../../components/LanguageSwitcher.tsx";
import LogoSvg from "../../assets/logo.svg?react";
import {
  desktopActions,
  drawerClose,
  footer,
  header,
  headerInner,
  headerLeft,
  logoIcon,
  logoLink,
  logoText,
  mobileDrawer,
  mobileMenuBtn,
} from "../landing/LandingPage.styles.ts";
import { footerLine, footerSeparator, legalContent, legalPage, logoHomeLink } from "./LegalPage.styles.ts";

const { Text } = Typography;

type Props = {
  readonly children: ReactNode;
};

export const LegalPageLayout = ({ children }: Props) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const { data, loading } = useQuery(GetCurrentLoggedUserDocument);
  const isLoggedIn = !!data?.getCurrentLoggedUser;

  const headerButtons = isLoggedIn ? (
    <Link to={routes.recipes.to()}>
      <Button icon={<ArrowRightOutlined />} iconPlacement="end" type="primary">
        {t("landing.openApp")}
      </Button>
    </Link>
  ) : loading ? (
    <Button disabled loading type="primary">
      {t("landing.openApp")}
    </Button>
  ) : (
    <Link to={routes.login.to()}>
      <Button type="primary">{t("landing.signIn")}</Button>
    </Link>
  );

  return (
    <div className={legalPage}>
      <div className={header}>
        <Flex align="center" className={headerInner} justify="space-between">
          <Flex align="center" className={headerLeft}>
            <Link className={logoHomeLink} to={routes.landing.to()}>
              <LogoSvg className={logoIcon} />
              <Text className={logoText} strong>
                Calculabel
              </Text>
            </Link>
          </Flex>
          <Flex align="center" className={desktopActions}>
            <LanguageSwitcher collapsed />
            {headerButtons}
          </Flex>
          <Button className={mobileMenuBtn} icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} />
        </Flex>
      </div>
      <Drawer
        className={mobileDrawer}
        classNames={{ close: drawerClose }}
        destroyOnHidden
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        placement="right"
        size={260}
        title={
          <Flex align="center" className={logoLink}>
            <LogoSvg className={logoIcon} />
            <Text className={logoText} strong>
              Calculabel
            </Text>
          </Flex>
        }
      >
        <Flex gap="small" vertical>
          {headerButtons}
          <LanguageSwitcher />
        </Flex>
      </Drawer>

      <div className={legalContent}>{children}</div>

      <div className={footer}>
        <div className={footerLine}>
          <Text type="secondary">{t("landing.footer")}</Text>
          <span className={footerSeparator}>·</span>
          <Link to={routes.legal.to()}>{t("common.legal")}</Link>
          <span className={footerSeparator}>·</span>
          <Link to={routes.privacy.to()}>{t("common.privacy")}</Link>
          <span className={footerSeparator}>·</span>
          <Link to={routes.terms.to()}>{t("common.terms")}</Link>
        </div>
      </div>
    </div>
  );
};
